/**
 * Where he is along the ride, once he is on the train.
 *
 * The cards answer "which train"; this answers "how far along it am I" — the next platform, how
 * many are left, and when he gets off. Logged before it is shown, so the numbers can be checked
 * against a real ride rather than trusted because they type-check.
 *
 * Pure over stops already resolved to epoch milliseconds: the GTFS service-day arithmetic and the
 * realtime merge belong to the worker, and doing them here would make this untestable.
 */

import type { RideProgress, RideStop } from "../shared/types";

/** Inside this, "two stops away" has stopped being the useful thing to say. */
export const ARRIVING_SECONDS = 45;

/**
 * Narrow the profile's destinations to the stop he actually chose.
 *
 * The ride home reaches Civic Center before Glen Park, so without this the earlier of the two is
 * always reported as his. An unrecognised choice is ignored rather than trusted, so a value off
 * the wire cannot name a stop this commute never reaches.
 */
export function chosenDestinations(profileDestinations: string[], chosenStopId: string | null | undefined): string[] {
	return chosenStopId && profileDestinations.includes(chosenStopId) ? [chosenStopId] : profileDestinations;
}

function timeOf(stop: RideStop): number {
	return stop.predictedMs ?? stop.scheduledMs;
}

/**
 * @param stops Every tracked stop on the trip, in sequence order.
 * @param destinations Where he might get off, narrowed to his choice when he has made one.
 */
export function rideProgress(
	tripId: string,
	stops: RideStop[],
	destinations: string[],
	nowMs: number,
): RideProgress {
	const ahead = stops.filter((stop) => timeOf(stop) > nowMs);
	const next = ahead[0] ?? null;
	// The first of these still in front of him. The caller narrows the list to the stop he chose,
	// so on the ride home this is his and not simply the earlier of the two.
	const destination = ahead.find((stop) => destinations.includes(stop.stopId)) ?? null;
	const secondsToNext = next == null ? null : Math.round((timeOf(next) - nowMs) / 1000);

	return {
		tripId,
		nextStopId: next?.stopId ?? null,
		nextStopName: next?.name ?? null,
		secondsToNext,
		arriving: secondsToNext != null && secondsToNext <= ARRIVING_SECONDS,
		destinationStopId: destination?.stopId ?? null,
		destinationName: destination?.name ?? null,
		stopsToGo: destination == null ? null : ahead.indexOf(destination) + 1,
		destinationEtaMs: destination == null ? null : timeOf(destination),
		stopsBehind: stops.length - ahead.length,
	};
}
